import {
  Badge,
  Button,
  Card,
  Group,
  Paper,
  Progress,
  RingProgress,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCalendar, IconChartBar, IconCoin, IconPlus, IconTags, IconUserPlus } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useProjectSummary } from "@/hooks/useProjectSummary";
import { useCreateBudgetLine } from "@/hooks/useBudget";
import { BudgetLineForm, type BudgetLineFormData } from "@/components/BudgetLineForm";
import { Modal } from "@mantine/core";
import { AIInsightsPanel } from "@/components/AIInsightsPanel";
import { ProjectTimeline } from "@/components/ProjectTimeline";
import { InviteTeamModal } from "@/components/InviteTeamModal";

export const Route = createFileRoute("/projects/$projectId/" as any)({
  component: ProjectOverviewPage,
});

function ProjectOverviewPage() {
  const params = Route.useParams();
  const projectId = "projectId" in params ? params.projectId : "";
  const navigate = useNavigate();
  const { data: summary, isLoading } = useProjectSummary(projectId);
  const createBudgetLine = useCreateBudgetLine(projectId);
  const [addBudgetOpened, { open: openAddBudget, close: closeAddBudget }] = useDisclosure(false);
  const [inviteOpened, { open: openInvite, close: closeInvite }] = useDisclosure(false);

  const handleAddBudgetLine = async (formData: BudgetLineFormData) => {
    try {
      await createBudgetLine.mutateAsync({
        costCodeId: formData.costCodeId,
        description: formData.description,
        quantity: formData.quantity,
        unit: formData.unit,
        unitCost: formData.unitCost,
      });
      notifications.show({
        title: "Success",
        message: "Budget line added!",
        color: "green",
      });
      closeAddBudget();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to add budget line",
        color: "red",
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "green";
      case "completed":
        return "blue";
      case "archived":
        return "gray";
      default:
        return "gray";
    }
  };

  // Color palette for divisions
  const divisionColors = [
    "#339af0", // blue
    "#51cf66", // green
    "#ff6b6b", // red
    "#ffd43b", // yellow
    "#845ef7", // violet
  ];

  if (isLoading) {
    return (
      <Stack gap="lg">
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
          <Skeleton height={120} radius="md" />
          <Skeleton height={120} radius="md" />
          <Skeleton height={120} radius="md" />
          <Skeleton height={120} radius="md" />
        </SimpleGrid>
        <Skeleton height={300} radius="md" />
        <Skeleton height={200} radius="md" />
      </Stack>
    );
  }

  if (!summary) {
    return (
      <Paper p="xl" withBorder>
        <Text c="dimmed">Failed to load project summary</Text>
      </Paper>
    );
  }

  const project = summary.project;
  const hasBudget = summary.totalBudget > 0;

  return (
    <>
      <Stack gap="lg">
        {/* Summary Cards */}
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
          <Card withBorder padding="lg">
            <Stack gap="xs">
              <Group gap="xs">
                <IconChartBar size={20} style={{ color: "var(--mantine-color-blue-6)" }} />
                <Text size="sm" c="dimmed" fw={500}>
                  Status
                </Text>
              </Group>
              <Badge color={getStatusColor(project.status)} variant="light" size="lg">
                {project.status}
              </Badge>
            </Stack>
          </Card>

          <Card withBorder padding="lg">
            <Stack gap="xs">
              <Group gap="xs">
                <IconCoin size={20} style={{ color: "var(--mantine-color-green-6)" }} />
                <Text size="sm" c="dimmed" fw={500}>
                  Total Budget
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {formatCurrency(summary.totalBudget)}
              </Text>
              <Text size="xs" c="dimmed">
                {summary.totalBudgetLines} line{summary.totalBudgetLines !== 1 ? "s" : ""}
              </Text>
            </Stack>
          </Card>

          <Card withBorder padding="lg">
            <Stack gap="xs">
              <Group gap="xs">
                <IconTags size={20} style={{ color: "var(--mantine-color-violet-6)" }} />
                <Text size="sm" c="dimmed" fw={500}>
                  Cost Codes
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {summary.costCodeCount}
              </Text>
              <Text size="xs" c="dimmed">
                assigned
              </Text>
            </Stack>
          </Card>

          <Card withBorder padding="lg">
            <Stack gap="xs">
              <Group gap="xs">
                <IconCalendar size={20} style={{ color: "var(--mantine-color-gray-6)" }} />
                <Text size="sm" c="dimmed" fw={500}>
                  Created
                </Text>
              </Group>
              <Text size="sm" fw={600}>
                {formatDate(project.createdAt)}
              </Text>
            </Stack>
          </Card>
        </SimpleGrid>

        {/* AI Insights Panel */}
        <AIInsightsPanel projectId={projectId} />

        {/* Project Timeline */}
        <ProjectTimeline projectStartDate={project.createdAt} />

        {/* Budget Summary Section */}
        <Paper p="xl" withBorder>
          <Stack gap="lg">
            <Group justify="space-between" align="center">
              <Title order={3}>Budget Summary</Title>
              <Button
                variant="light"
                size="sm"
                onClick={() =>
                  navigate({ to: "/projects/$projectId/budget", params: { projectId } })
                }
              >
                View Full Budget
              </Button>
            </Group>

            {!hasBudget ? (
              <Paper p="xl" withBorder style={{ borderStyle: "dashed" }}>
                <Stack gap="md" align="center">
                  <Text c="dimmed">No budget lines yet. Start by adding your first budget line.</Text>
                  <Button leftSection={<IconPlus size={16} />} onClick={openAddBudget}>
                    Add Budget Line
                  </Button>
                </Stack>
              </Paper>
            ) : (
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
                {/* Division Ring Progress */}
                <Card withBorder padding="lg">
                  <Stack gap="md" align="center">
                    <Text size="sm" fw={500}>
                      Budget by Division
                    </Text>
                    <RingProgress
                      size={200}
                      thickness={20}
                      sections={summary.topDivisions.map((div, idx) => ({
                        value: div.percentage,
                        color: divisionColors[idx % divisionColors.length] || "#339af0",
                        tooltip: `${div.division}: ${formatCurrency(div.total)} (${div.percentage.toFixed(1)}%)`,
                      }))}
                      label={
                        <div style={{ textAlign: "center" }}>
                          <Text size="xl" fw={700}>
                            {formatCurrency(summary.totalBudget)}
                          </Text>
                          <Text size="xs" c="dimmed">
                            Total
                          </Text>
                        </div>
                      }
                    />
                  </Stack>
                </Card>

                {/* Top Divisions List */}
                <Card withBorder padding="lg">
                  <Stack gap="md">
                    <Text size="sm" fw={500}>
                      Top 5 Divisions
                    </Text>
                    <Stack gap="sm">
                      {summary.topDivisions.map((div, idx) => (
                        <div key={div.division}>
                          <Group justify="space-between" mb={4}>
                            <Group gap="xs">
                              <div
                                style={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  backgroundColor: divisionColors[idx % divisionColors.length],
                                }}
                              />
                              <Text size="sm" fw={500}>
                                {div.division}
                              </Text>
                            </Group>
                            <Text size="sm" fw={600}>
                              {formatCurrency(div.total)}
                            </Text>
                          </Group>
                          <Progress
                            value={div.percentage}
                            color={divisionColors[idx % divisionColors.length]}
                            size="sm"
                          />
                        </div>
                      ))}
                    </Stack>
                  </Stack>
                </Card>
              </SimpleGrid>
            )}
          </Stack>
        </Paper>

        {/* Quick Actions Section */}
        <Paper p="xl" withBorder>
          <Stack gap="lg">
            <Title order={3}>Quick Actions</Title>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
              <Button
                variant="light"
                leftSection={<IconPlus size={18} />}
                onClick={openAddBudget}
                size="md"
              >
                Add Budget Line
              </Button>
              <Button
                variant="light"
                leftSection={<IconTags size={18} />}
                onClick={() =>
                  navigate({ to: "/projects/$projectId/cost-codes", params: { projectId } })
                }
                size="md"
              >
                Manage Cost Codes
              </Button>
              <Button
                variant="light"
                leftSection={<IconChartBar size={18} />}
                onClick={() =>
                  navigate({ to: "/projects/$projectId/budget", params: { projectId } })
                }
                size="md"
              >
                View Budget Details
              </Button>
              <Button
                variant="light"
                leftSection={<IconUserPlus size={18} />}
                onClick={openInvite}
                size="md"
              >
                Invite Team
              </Button>
            </SimpleGrid>
          </Stack>
        </Paper>

        {/* Project Details */}
        {(project.address || project.city || project.state || project.zip) && (
          <Paper p="xl" withBorder>
            <Stack gap="md">
              <Title order={3}>Project Details</Title>
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Address
                </Text>
                <Text c="dimmed">{project.address || "No address"}</Text>
                <Text c="dimmed">
                  {[project.city, project.state, project.zip].filter(Boolean).join(", ") ||
                    "No location"}
                </Text>
              </Stack>
            </Stack>
          </Paper>
        )}
      </Stack>

      {/* Add Budget Line Modal */}
      <Modal
        opened={addBudgetOpened}
        onClose={closeAddBudget}
        title="Add Budget Line"
        size="lg"
        centered
      >
        <BudgetLineForm
          onSubmit={handleAddBudgetLine}
          onCancel={closeAddBudget}
          loading={createBudgetLine.isPending}
          submitText="Add Line"
          projectId={projectId}
        />
      </Modal>

      {/* Invite Team Modal */}
      <InviteTeamModal
        projectId={projectId}
        projectName={summary?.project?.name || "Project"}
        isOpen={inviteOpened}
        onClose={closeInvite}
      />
    </>
  );
}
